import '.b9{border:none;}.ssr-plugin'
import '.ba{background-color:transparent;}.ssr-plugin'
import '.bb{padding:0px;}.ssr-plugin'
import '.a{display:inline-flex;}.ssr-plugin'
import '.a7{flex-direction:row;}.ssr-plugin'
import '.p{justify-content:center;}.ssr-plugin'
import '.b{align-items:center;}.ssr-plugin'
import '.bc{border-radius:1.875rem;}.ssr-plugin'
import '.bd{font-weight:600;}.ssr-plugin'
import '.ap{cursor:pointer;}.ssr-plugin'
import { styled, type Style } from 'inlines'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { type IconDefinition } from '@fortawesome/pro-solid-svg-icons'
import {
  linkIconSizeStyle,
  linkSizeStyle,
  linkStyle,
} from '../link/linkStyleHeper'
import { Text } from '../typography/Text'
import { MouseEventHandler } from 'react'

export type ButtonProps = {
  variant?: Variant
  size?: Size
  label?: string
  icon?: IconDefinition
  iconPosition?: 'left' | 'right'
  disabled?: boolean
  style?: Style
  textStyle?: Style
  onClick?: MouseEventHandler<HTMLButtonElement>
  onMouseDown?: MouseEventHandler<HTMLButtonElement>
}

type Size = 'small' | 'medium'
type Variant = 'primary' | 'secondary' | 'link' | 'bold-link'

const sizeStyle: Record<Size, Style> = {
  medium: {
    height: '3rem',
    paddingLeft: '1.5rem',
    paddingRight: '1.5rem',
    gap: '0.5rem',
    fontSize: '1.125rem',
  },
  small: {
    height: '2rem',
    paddingLeft: '1rem',
    paddingRight: '1rem',
    gap: '0.25rem',
    fontSize: '1rem',
  },
}
const sizeIconStyle: Record<Size, Style> = {
  medium: {
    height: '1.25rem',
    width: '1.25rem',
  },
  small: {
    height: '1rem',
    width: '1rem',
  },
}
const variantStyle: Record<Variant, Style> = {
  primary: {
    color: 'var(--shades-white)',
    background: 'var(--primary-red)',
    border: '2px solid var(--primary-red)',
    paddingTop: '0.75rem',
    paddingBottom: '0.75rem',
    '&:hover': {
      background: 'var(--shades-red-600)',
      border: '2px solid var(--shades-red-600)',
    },
    '&:active': {
      background: 'var(--shades-red-600)',
      border: '2px solid var(--shades-red-600)',
    },
    '&:disabled': {
      background: 'var(--shades-red-200)',
      border: '2px solid var(--shades-red-200)',
    },
  },
  secondary: {
    color: 'var(--primary-blue)',
    background: 'transparent',
    border: '2px solid var(--primary-blue)',
    paddingTop: '0.8125rem',
    paddingBottom: '0.8125rem',
    '&:hover': {
      background: 'var(--primary-blue)',
      color: 'var(--shades-white)',
    },
    '&:active': {
      background: 'var(--primary-blue)',
      color: 'var(--shades-white)',
    },
    '&:disabled': {
      border: '2px solid var(--shades-blue-200)',
      color: 'var(--shades-blue-200)',
    },
    '&:hover:disabled': {
      background: 'transparent',
      cursor: 'not-allowed',
    },
  },
  link: { fontWeight: 500 },
  'bold-link': { fontWeight: 600 },
}

export const Button = ({
  variant = 'primary',
  size = 'medium',
  label = undefined,
  icon,
  iconPosition = 'right',
  disabled = false,
  style,
  textStyle,
  onClick,
  onMouseDown,
}: ButtonProps) => {
  const faIcon = icon ? renderIcon(icon, variant, size) : undefined

  if (variant === 'link' || variant === 'bold-link') {
    const text = label ? (
      <Text.Medium
        key="text"
        style={{
          ...linkSizeStyle[size],
          ...variantStyle[variant],
          ...textStyle,
        }}
      >
        {label}
      </Text.Medium>
    ) : undefined
    return (
      <styled.button className="b9 ba bb"
        onClick={onClick}
        onMouseDown={onMouseDown}
        style={{
          ...linkStyle,
          ...style,
        }}
      >
        {renderContent(text, faIcon, iconPosition)}
      </styled.button>
    )
  }

  return (
    <styled.button className="a a7 p b bc bd ap"
      disabled={disabled}
      onClick={onClick}
      onMouseDown={onMouseDown}
      style={{
        '&:hover:disabled': {
          cursor: 'not-allowed',
        },
        ...variantStyle[variant],
        ...sizeStyle[size],
        ...style,
      }}
    >
      {renderContent(label, faIcon, iconPosition)}
    </styled.button>
  )
}

const renderIcon = (icon: IconDefinition, variant: Variant, size: Size) => {
  if (variant === 'link') {
    return (
      <FontAwesomeIcon
        key="icon"
        icon={icon}
        style={{ ...linkIconSizeStyle[size] }}
      />
    )
  }

  return (
    <FontAwesomeIcon
      key="icon"
      icon={icon}
      style={{ ...sizeIconStyle[size] }}
    />
  )
}

const renderContent = (
  text?: JSX.Element | string,
  icon?: JSX.Element,
  iconPosition?: 'left' | 'right',
) => {
  const hasText = text !== undefined && text !== ''
  const hasIcon = icon !== undefined

  if (!hasText && !hasIcon) {
    throw Error('The button has neither text nor icon')
  }

  if (!hasText || !hasIcon) {
    return hasText ? text : icon
  }
  return iconPosition === 'right' ? [text, icon] : [icon, text]
}
